import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from 'react-i18next';
import axios from "../../axiosConfig";
import {
    FiCalendar,
    FiClock,
    FiMapPin,
    FiPlus,
    FiList,
    FiEdit2,
    FiTrash2,
    FiX,
    FiSave,
    FiCheckCircle,
    FiCheck,
    FiUserX,
    FiRepeat
} from "react-icons/fi";
import { PiDog } from "react-icons/pi";
import { format } from "date-fns";
import { pl, enUS } from "date-fns/locale";
import { CustomDatePicker, CustomTimePicker } from '../commons/datepicker';
import { TrainerContext } from "../../context/TrainerContext";

const GroupTrainings = ({ groupId }) => {
    const { t, i18n } = useTranslation(['groups', 'common']);
    const { trainer } = useContext(TrainerContext);
    const [trainings, setTrainings] = useState([]);
    const [pastTrainings, setPastTrainings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pastLoading, setPastLoading] = useState(false);
    const [error, setError] = useState("");
    const [activeTab, setActiveTab] = useState("upcoming");
    const [isBookModalOpen, setIsBookModalOpen] = useState(false);
    const [isMultipleBookModalOpen, setIsMultipleBookModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isAttendanceModalOpen, setIsAttendanceModalOpen] = useState(false);
    const [isCompleteConfirmOpen, setIsCompleteConfirmOpen] = useState(false);
    const [isRecurringModalOpen, setIsRecurringModalOpen] = useState(false);
    const [recurringSubmitting, setRecurringSubmitting] = useState(false);
    const [halls, setHalls] = useState([]);
    const [currentTraining, setCurrentTraining] = useState(null);
    const [attendanceData, setAttendanceData] = useState([]);
    const [attendanceNotes, setAttendanceNotes] = useState({});
    const [savingAttendance, setSavingAttendance] = useState(false);

    // Form states for single booking
    const [bookingForm, setBookingForm] = useState({
        hallId: "",
        date: "",
        startTime: "",
        endTime: "",
    });

    // Form states for multiple bookings
    const [multipleBookingForm, setMultipleBookingForm] = useState({
        hallId: "",
        timeSlots: [{ date: "", startTime: "", endTime: "" }]
    });

    // Edit form state
    const [editForm, setEditForm] = useState({
        hallId: "",
        date: "",
        startTime: "",
        endTime: "",
        notes: ""
    });

    // Recurring form state
    const [recurringForm, setRecurringForm] = useState({
        patternType: "WEEKLY",
        daysOfWeek: [],
        startTime: "",
        endTime: "",
        startDate: "",
        endType: "BY_COUNT",
        endDate: "",
        occurrenceCount: 8,
        hallId: "",
        notes: ""
    });

    useEffect(() => {
        fetchTrainings();
        fetchHalls();
    }, [groupId]);

    const fetchTrainings = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("jwt");
            const response = await axios.get(`/api/v1/group-trainings/group/${groupId}/upcoming`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const sortedData = [...response.data].sort((a, b) =>
                new Date(a.scheduledAt) - new Date(b.scheduledAt)
            );
            setTrainings(sortedData);
        } catch (err) {
            setError(t('trainings.fetchError'));
        } finally {
            setLoading(false);
        }
    };

    const fetchPastTrainings = async () => {
        try {
            setPastLoading(true);
            const token = localStorage.getItem("jwt");
            const response = await axios.get(`/api/v1/group-trainings/group/${groupId}/past`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const sortedData = [...response.data].sort((a, b) =>
                new Date(b.scheduledAt) - new Date(a.scheduledAt)
            );
            setPastTrainings(sortedData);
        } catch (err) {
            console.error("Error fetching past trainings:", err);
        } finally {
            setPastLoading(false);
        }
    };

    const fetchHalls = async () => {
        try {
            const token = localStorage.getItem("jwt");
            const response = await axios.get(`/api/v1/school/${trainer.schoolId}/halls`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setHalls(response.data || []);

            // Set default hall if available
            if (response.data && response.data.length > 0) {
                setBookingForm(prev => ({ ...prev, hallId: response.data[0].id }));
                setMultipleBookingForm(prev => ({ ...prev, hallId: response.data[0].id }));
            }
        } catch (err) {
            console.error("Error fetching halls:", err);
            setHalls([]);
        }
    };

    // Helper to show notifications
    const showNotification = (message, type = "success") => {
        const notification = document.createElement("div");
        notification.className = type === "success" ? "success-notification" : "error-notification";
        notification.textContent = message;
        document.body.appendChild(notification);
        setTimeout(() => {
            document.body.removeChild(notification);
        }, 3000);
    };

    // Localize English day names in backend error messages
    const localizeDayNames = (message) => {
        if (!message || !i18n.language?.startsWith('pl')) return message;
        const dayMap = {
            'MONDAY': 'poniedziałek',
            'TUESDAY': 'wtorek',
            'WEDNESDAY': 'środa',
            'THURSDAY': 'czwartek',
            'FRIDAY': 'piątek',
            'SATURDAY': 'sobota',
            'SUNDAY': 'niedziela'
        };
        return message.replace(
            /\b(MONDAY|TUESDAY|WEDNESDAY|THURSDAY|FRIDAY|SATURDAY|SUNDAY)\b/g,
            (match) => dayMap[match] || match
        );
    };

    // Extract detailed error message from API response
    const getErrorMessage = (err, fallback) => {
        const data = err.response?.data;
        if (!data) return fallback;
        // Check for detailed errors in the errors object
        if (data.errors) {
            const errorMessages = Object.values(data.errors).filter(Boolean);
            if (errorMessages.length > 0) return localizeDayNames(errorMessages.join('. '));
        }
        return localizeDayNames(data.message) || fallback;
    };

    // Format date for display
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const locale = i18n.language?.startsWith('pl') ? pl : enUS;
        return format(date, "EEEE, d MMM yyyy", { locale });
    };

    // Format time for display
    const formatTime = (dateString) => {
        const date = new Date(dateString);
        return format(date, "HH:mm");
    };

    // Calculate end time from scheduledAt + durationMinutes
    const getEndTime = (training) => {
        const startDate = new Date(training.scheduledAt);
        const endDate = new Date(startDate.getTime() + (training.durationMinutes || 60) * 60000);
        return format(endDate, "HH:mm");
    };

    // Check if a training is in the past
    const isTrainingPast = (training) => {
        const endTime = new Date(training.scheduledAt).getTime() + (training.durationMinutes || 60) * 60000;
        return endTime < Date.now();
    };

    // Handle tab switch
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        if (tab === "history" && pastTrainings.length === 0) {
            fetchPastTrainings();
        }
    };

    // Handle single booking form changes
    const handleBookingFormChange = (e) => {
        const { name, value } = e.target;
        setBookingForm(prev => ({ ...prev, [name]: value }));
    };

    // Handle multiple booking form changes
    const handleMultipleBookingFormChange = (e) => {
        const { name, value } = e.target;
        setMultipleBookingForm(prev => ({ ...prev, [name]: value }));
    };

    // Handle time slot changes in multiple booking
    const handleTimeSlotChange = (index, field, value) => {
        const updatedSlots = [...multipleBookingForm.timeSlots];
        updatedSlots[index] = { ...updatedSlots[index], [field]: value };
        setMultipleBookingForm(prev => ({ ...prev, timeSlots: updatedSlots }));
    };

    // Add new time slot in multiple booking
    const addTimeSlot = () => {
        setMultipleBookingForm(prev => ({
            ...prev,
            timeSlots: [...prev.timeSlots, { date: "", startTime: "", endTime: "" }]
        }));
    };

    // Remove time slot in multiple booking
    const removeTimeSlot = (index) => {
        if (multipleBookingForm.timeSlots.length === 1) return;
        const updatedSlots = multipleBookingForm.timeSlots.filter((_, i) => i !== index);
        setMultipleBookingForm(prev => ({ ...prev, timeSlots: updatedSlots }));
    };

    // Handle edit form changes
    const handleEditFormChange = (e) => {
        const { name, value } = e.target;
        setEditForm(prev => ({ ...prev, [name]: value }));
    };

    // Open edit modal and set current training
    const openEditModal = (training) => {
        const startDate = new Date(training.scheduledAt);
        const endDate = new Date(startDate.getTime() + (training.durationMinutes || 60) * 60000);

        setCurrentTraining(training);
        setEditForm({
            hallId: training.hallId || halls.find(h => h.name === training.hallName)?.id || "",
            date: format(startDate, "yyyy-MM-dd"),
            startTime: format(startDate, "HH:mm"),
            endTime: format(endDate, "HH:mm"),
            notes: training.notes || ""
        });
        setIsEditModalOpen(true);
    };

    // Open delete confirmation modal
    const openDeleteModal = (training) => {
        setCurrentTraining(training);
        setIsDeleteModalOpen(true);
    };

    // Open attendance modal - fetch full training details with participations
    const openAttendanceModal = async (training) => {
        try {
            const token = localStorage.getItem("jwt");
            const response = await axios.get(`/api/v1/group-trainings/${training.id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const fullTraining = response.data;
            setCurrentTraining(fullTraining);
            setAttendanceData(
                (fullTraining.dogParticipations || []).map(dp => ({
                    ...dp,
                    newStatus: dp.status
                }))
            );
            // Initialize notes from existing absence notes
            const notes = {};
            (fullTraining.dogParticipations || []).forEach(dp => {
                if (dp.absenceNote) {
                    notes[dp.dogId] = dp.absenceNote;
                }
            });
            setAttendanceNotes(notes);
            setIsAttendanceModalOpen(true);
        } catch (err) {
            showNotification(t('trainings.fetchError'), "error");
        }
    };

    // Open complete training confirmation (with attendance)
    const openCompleteConfirm = async (training) => {
        await openAttendanceModal(training);
        setIsCompleteConfirmOpen(true);
    };

    // Toggle attendance status for a dog
    const toggleAttendance = (dogId) => {
        setAttendanceData(prev => prev.map(dp => {
            if (dp.dogId === dogId) {
                const newStatus = dp.newStatus === "ABSENT" ? "SCHEDULED" : "ABSENT";
                return { ...dp, newStatus };
            }
            return dp;
        }));
    };

    // Save attendance changes
    const saveAttendance = async () => {
        setSavingAttendance(true);
        try {
            const token = localStorage.getItem("jwt");
            const headers = { Authorization: `Bearer ${token}` };

            for (const dp of attendanceData) {
                if (dp.newStatus !== dp.status) {
                    if (dp.newStatus === "ABSENT") {
                        await axios.patch(
                            `/api/v1/group-trainings/${currentTraining.id}/dogs/${dp.dogId}/absence`,
                            { note: attendanceNotes[dp.dogId] || null },
                            { headers }
                        );
                    } else if (dp.status === "ABSENT" && dp.newStatus !== "ABSENT") {
                        await axios.delete(
                            `/api/v1/group-trainings/${currentTraining.id}/dogs/${dp.dogId}/absence`,
                            { headers }
                        );
                    }
                }
            }

            showNotification(t('trainings.attendanceSaved'));
            setIsAttendanceModalOpen(false);
            setIsCompleteConfirmOpen(false);
            fetchTrainings();
            if (activeTab === "history") fetchPastTrainings();
        } catch (err) {
            showNotification(getErrorMessage(err, t('trainings.attendanceError')), "error");
        } finally {
            setSavingAttendance(false);
        }
    };

    // Complete training and save attendance
    const completeTraining = async () => {
        setSavingAttendance(true);
        try {
            const token = localStorage.getItem("jwt");
            const headers = { Authorization: `Bearer ${token}` };

            // First save any absence marks
            for (const dp of attendanceData) {
                if (dp.newStatus === "ABSENT" && dp.status !== "ABSENT") {
                    await axios.patch(
                        `/api/v1/group-trainings/${currentTraining.id}/dogs/${dp.dogId}/absence`,
                        { note: attendanceNotes[dp.dogId] || null },
                        { headers }
                    );
                } else if (dp.status === "ABSENT" && dp.newStatus !== "ABSENT") {
                    await axios.delete(
                        `/api/v1/group-trainings/${currentTraining.id}/dogs/${dp.dogId}/absence`,
                        { headers }
                    );
                }
            }

            // Then mark training as completed
            await axios.patch(
                `/api/v1/group-trainings/${currentTraining.id}/complete`,
                {},
                { headers }
            );

            showNotification(t('trainings.completed'));
            setIsAttendanceModalOpen(false);
            setIsCompleteConfirmOpen(false);
            fetchTrainings();
            if (activeTab === "history") fetchPastTrainings();
        } catch (err) {
            showNotification(getErrorMessage(err, t('trainings.completeError')), "error");
        } finally {
            setSavingAttendance(false);
        }
    };

    // Book a single training
    const bookSingleTraining = async () => {
        if (!bookingForm.date || !bookingForm.startTime || !bookingForm.endTime) {
            showNotification(t('trainings.dateAndTimesRequired'), "error");
            return;
        }

        try {
            const token = localStorage.getItem("jwt");

            const startParts = bookingForm.startTime.split(':');
            const endParts = bookingForm.endTime.split(':');
            const startMinutes = parseInt(startParts[0]) * 60 + parseInt(startParts[1]);
            const endMinutes = parseInt(endParts[0]) * 60 + parseInt(endParts[1]);
            const durationMinutes = endMinutes - startMinutes;

            if (durationMinutes < 15) {
                showNotification(t('trainings.minDuration'), "error");
                return;
            }

            const payload = {
                groupId: groupId,
                scheduledAt: `${bookingForm.date}T${bookingForm.startTime}:00`,
                durationMinutes: durationMinutes
            };

            if (bookingForm.hallId) {
                payload.hallId = bookingForm.hallId;
            }

            await axios.post('/api/v1/group-trainings', payload, {
                headers: { Authorization: `Bearer ${token}` },
            });

            showNotification(t('trainings.booked'));
            setIsBookModalOpen(false);
            fetchTrainings();

            setBookingForm({
                hallId: halls.length > 0 ? halls[0].id : "",
                date: "",
                startTime: "",
                endTime: "",
            });
        } catch (err) {
            showNotification(getErrorMessage(err, t('trainings.bookError')), "error");
        }
    };

    // Book multiple trainings
    const bookMultipleTrainings = async () => {
        const isValid = multipleBookingForm.timeSlots.every(slot =>
            slot.date && slot.startTime && slot.endTime
        );

        if (!isValid) {
            showNotification(t('trainings.fillAllSlots'), "error");
            return;
        }

        try {
            const token = localStorage.getItem("jwt");
            const headers = { Authorization: `Bearer ${token}` };

            for (const slot of multipleBookingForm.timeSlots) {
                const startParts = slot.startTime.split(':');
                const endParts = slot.endTime.split(':');
                const startMinutes = parseInt(startParts[0]) * 60 + parseInt(startParts[1]);
                const endMinutes = parseInt(endParts[0]) * 60 + parseInt(endParts[1]);
                const durationMinutes = endMinutes - startMinutes;

                const payload = {
                    groupId: groupId,
                    scheduledAt: `${slot.date}T${slot.startTime}:00`,
                    durationMinutes: durationMinutes
                };

                if (multipleBookingForm.hallId) {
                    payload.hallId = multipleBookingForm.hallId;
                }

                await axios.post('/api/v1/group-trainings', payload, { headers });
            }

            showNotification(t('trainings.multipleBooked'));
            setIsMultipleBookModalOpen(false);
            fetchTrainings();

            setMultipleBookingForm({
                hallId: halls.length > 0 ? halls[0].id : "",
                timeSlots: [{ date: "", startTime: "", endTime: "" }]
            });
        } catch (err) {
            showNotification(getErrorMessage(err, t('trainings.multipleBookError')), "error");
        }
    };

    // Book recurring trainings
    const bookRecurringTrainings = async () => {
        if (recurringForm.daysOfWeek.length === 0) {
            showNotification(t('trainings.selectDaysRequired'), "error");
            return;
        }
        if (!recurringForm.startTime || !recurringForm.endTime) {
            showNotification(t('trainings.dateAndTimesRequired'), "error");
            return;
        }
        if (!recurringForm.startDate) {
            showNotification(t('trainings.startDateRequired'), "error");
            return;
        }

        const startParts = recurringForm.startTime.split(':');
        const endParts = recurringForm.endTime.split(':');
        const startMinutes = parseInt(startParts[0]) * 60 + parseInt(startParts[1]);
        const endMinutes = parseInt(endParts[0]) * 60 + parseInt(endParts[1]);
        const durationMinutes = endMinutes - startMinutes;

        if (durationMinutes < 15) {
            showNotification(t('trainings.minDuration'), "error");
            return;
        }

        if (recurringForm.endType === "BY_DATE" && !recurringForm.endDate) {
            showNotification(t('trainings.endDateRequired'), "error");
            return;
        }

        setRecurringSubmitting(true);
        try {
            const token = localStorage.getItem("jwt");
            const payload = {
                groupId: groupId,
                patternType: recurringForm.patternType,
                daysOfWeek: recurringForm.daysOfWeek,
                timeOfDay: recurringForm.startTime + ":00",
                durationMinutes: durationMinutes,
                startDate: recurringForm.startDate,
                endType: recurringForm.endType,
                notes: recurringForm.notes || null
            };

            if (recurringForm.endType === "BY_DATE") {
                payload.endDate = recurringForm.endDate;
            } else {
                payload.occurrenceCount = parseInt(recurringForm.occurrenceCount) || 8;
            }

            if (recurringForm.hallId) {
                payload.hallId = recurringForm.hallId;
            }

            const response = await axios.post('/api/v1/group-trainings/recurring', payload, {
                headers: { Authorization: `Bearer ${token}` },
            });

            const count = response.data?.length || 0;
            showNotification(t('trainings.recurringBooked', { count }));
            setIsRecurringModalOpen(false);
            fetchTrainings();

            setRecurringForm({
                patternType: "WEEKLY",
                daysOfWeek: [],
                startTime: "",
                endTime: "",
                startDate: "",
                endType: "BY_COUNT",
                endDate: "",
                occurrenceCount: 8,
                hallId: halls.length > 0 ? halls[0].id : "",
                notes: ""
            });
        } catch (err) {
            showNotification(getErrorMessage(err, t('trainings.recurringBookError')), "error");
        } finally {
            setRecurringSubmitting(false);
        }
    };

    // Toggle day of week for recurring form
    const toggleDayOfWeek = (day) => {
        setRecurringForm(prev => ({
            ...prev,
            daysOfWeek: prev.daysOfWeek.includes(day)
                ? prev.daysOfWeek.filter(d => d !== day)
                : [...prev.daysOfWeek, day]
        }));
    };

    // Update training
    const updateTraining = async () => {
        if (!editForm.date || !editForm.startTime || !editForm.endTime) {
            showNotification(t('trainings.dateAndTimesRequired'), "error");
            return;
        }

        try {
            const token = localStorage.getItem("jwt");

            const startParts = editForm.startTime.split(':');
            const endParts = editForm.endTime.split(':');
            const startMinutes = parseInt(startParts[0]) * 60 + parseInt(startParts[1]);
            const endMinutes = parseInt(endParts[0]) * 60 + parseInt(endParts[1]);
            const durationMinutes = endMinutes - startMinutes;

            if (durationMinutes < 15) {
                showNotification(t('trainings.minDuration'), "error");
                return;
            }

            const payload = {
                scheduledAt: `${editForm.date}T${editForm.startTime}:00`,
                durationMinutes: durationMinutes,
                notes: editForm.notes
            };

            if (editForm.hallId) {
                payload.hallId = editForm.hallId;
            }

            await axios.put(`/api/v1/group-trainings/${currentTraining.id}`, payload, {
                headers: { Authorization: `Bearer ${token}` },
            });

            showNotification(t('trainings.updated'));
            setIsEditModalOpen(false);
            fetchTrainings();
        } catch (err) {
            showNotification(getErrorMessage(err, t('trainings.updateError')), "error");
        }
    };

    // Delete training
    const deleteTraining = async () => {
        try {
            const token = localStorage.getItem("jwt");
            await axios.delete(`/api/v1/group-trainings/${currentTraining.id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            showNotification(t('trainings.deleted'));
            setIsDeleteModalOpen(false);
            fetchTrainings();
        } catch (err) {
            showNotification(getErrorMessage(err, t('trainings.deleteError')), "error");
        }
    };

    // Get status badge for a training
    const getStatusBadge = (training) => {
        if (training.status === "COMPLETED") {
            return <span className="training-status-badge completed">{t('trainings.statusCompleted')}</span>;
        }
        if (training.status === "CANCELED") {
            return <span className="training-status-badge canceled">{t('trainings.statusCanceled')}</span>;
        }
        if (isTrainingPast(training)) {
            return <span className="training-status-badge pending">{t('trainings.statusPendingComplete')}</span>;
        }
        return null;
    };

    // Get attendance summary for a training
    const getAttendanceSummary = (training) => {
        if (!training.dogParticipations) return null;
        const attended = training.dogParticipations.filter(dp => dp.status === "ATTENDED").length;
        const absent = training.dogParticipations.filter(dp => dp.status === "ABSENT").length;
        const total = training.dogParticipations.length;
        return { attended, absent, total };
    };

    // Render a training card
    const renderTrainingCard = (training, showActions = true) => {
        const isPast = isTrainingPast(training);
        const isCompleted = training.status === "COMPLETED";
        const isCanceled = training.status === "CANCELED";
        const attendanceSummary = getAttendanceSummary(training);

        const stateClass = isCompleted ? 'completed' : isCanceled ? 'canceled' : isPast ? 'past-pending' : '';

        return (
            <div key={training.id} className={`gt-card ${stateClass}`}>
                <div className="gt-card-left">
                    <div className="gt-date-badge">
                        <span className="gt-date-day">{format(new Date(training.scheduledAt), "d")}</span>
                        <span className="gt-date-month">{format(new Date(training.scheduledAt), "MMM", { locale: i18n.language?.startsWith('pl') ? pl : enUS })}</span>
                    </div>
                </div>
                <div className="gt-card-body">
                    <div className="gt-card-row-primary">
                        <span className="gt-time">
                            <FiClock className="gt-icon" />
                            {formatTime(training.scheduledAt)} – {getEndTime(training)}
                        </span>
                        {training.hallName && (
                            <span className="gt-hall">
                                <FiMapPin className="gt-icon" />
                                {training.hallName}
                            </span>
                        )}
                        {getStatusBadge(training)}
                    </div>
                    <div className="gt-card-row-secondary">
                        <span className="gt-weekday">{format(new Date(training.scheduledAt), "EEEE", { locale: i18n.language?.startsWith('pl') ? pl : enUS })}</span>
                        {attendanceSummary && (isCompleted || isPast) && (
                            <span className="gt-attendance">
                                <PiDog className="gt-icon" />
                                {t('trainings.attendanceSummary', {
                                    attended: attendanceSummary.attended,
                                    absent: attendanceSummary.absent,
                                    total: attendanceSummary.total
                                })}
                            </span>
                        )}
                    </div>
                </div>
                {showActions && (
                    <div className="gt-card-actions">
                        {!isCompleted && !isCanceled && isPast && (
                            <button
                                className="action-icon-btn complete"
                                onClick={() => openCompleteConfirm(training)}
                                title={t('trainings.completeAndAttendance')}
                            >
                                <FiCheckCircle />
                            </button>
                        )}
                        {!isCanceled && (
                            <button
                                className="action-icon-btn"
                                onClick={() => openAttendanceModal(training)}
                                title={isCompleted ? t('trainings.viewAttendance') : t('trainings.attendance')}
                            >
                                <PiDog />
                            </button>
                        )}
                        {!isCompleted && !isCanceled && !isPast && (
                            <>
                                <button
                                    className="action-icon-btn"
                                    onClick={() => openEditModal(training)}
                                    title={t('common:edit')}
                                >
                                    <FiEdit2 />
                                </button>
                                <button
                                    className="action-icon-btn delete"
                                    onClick={() => openDeleteModal(training)}
                                    title={t('common:delete')}
                                >
                                    <FiTrash2 />
                                </button>
                            </>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="modern-details-section">
            <h3 className="modern-section-title">
                <FiCalendar className="section-icon" />
                <span>{t('trainings.groupTrainings')}</span>
                <span className="modern-count">{trainings.length}</span>
            </h3>

            {/* Tab switcher */}
            <div className="training-tabs">
                <button
                    className={`training-tab ${activeTab === 'upcoming' ? 'active' : ''}`}
                    onClick={() => handleTabChange('upcoming')}
                >
                    {t('trainings.upcoming')}
                </button>
                <button
                    className={`training-tab ${activeTab === 'history' ? 'active' : ''}`}
                    onClick={() => handleTabChange('history')}
                >
                    {t('trainings.history')}
                </button>
            </div>

            {activeTab === 'upcoming' && (
                <>
                    <div className="modern-training-actions">
                        <button
                            className="modern-action-btn primary"
                            onClick={() => setIsBookModalOpen(true)}
                        >
                            <FiPlus className="icon" />
                            <span>{t('trainings.bookTraining')}</span>
                        </button>
                        <button
                            className="modern-action-btn secondary"
                            onClick={() => setIsMultipleBookModalOpen(true)}
                        >
                            <FiList className="icon" />
                            <span>{t('trainings.bookMultiple')}</span>
                        </button>
                        <button
                            className="modern-action-btn secondary"
                            onClick={() => {
                                setRecurringForm(prev => ({
                                    ...prev,
                                    hallId: halls.length > 0 ? halls[0].id : ""
                                }));
                                setIsRecurringModalOpen(true);
                            }}
                        >
                            <FiRepeat className="icon" />
                            <span>{t('trainings.bookRecurring')}</span>
                        </button>
                    </div>

                    {loading ? (
                        <div className="modern-loading-indicator">
                            <div className="spinner-small"></div>
                            <span>{t('trainings.loadingTrainings')}</span>
                        </div>
                    ) : error ? (
                        <div className="modern-error-message">{error}</div>
                    ) : trainings.length === 0 ? (
                        <div className="modern-empty-list">
                            {t('trainings.noUpcoming')}
                        </div>
                    ) : (
                        <div className="modern-trainings-list">
                            {trainings.map((training) => renderTrainingCard(training, true))}
                        </div>
                    )}
                </>
            )}

            {activeTab === 'history' && (
                <>
                    {pastLoading ? (
                        <div className="modern-loading-indicator">
                            <div className="spinner-small"></div>
                            <span>{t('trainings.loadingTrainings')}</span>
                        </div>
                    ) : pastTrainings.length === 0 ? (
                        <div className="modern-empty-list">
                            {t('trainings.noHistory')}
                        </div>
                    ) : (
                        <div className="modern-trainings-list">
                            {pastTrainings.map((training) => renderTrainingCard(training, true))}
                        </div>
                    )}
                </>
            )}

            {/* Book Single Training Modal */}
            {isBookModalOpen && (
                <div className="modern-modal-overlay">
                    <div className="modern-modal">
                        <div className="modern-modal-header">
                            <h2 className="modern-modal-title">{t('trainings.bookGroupTraining')}</h2>
                            <button
                                className="modern-close-btn"
                                onClick={() => setIsBookModalOpen(false)}
                            >
                                &times;
                            </button>
                        </div>

                        <div className="modern-modal-content">
                            {halls.length > 0 && (
                                <div className="modern-input-group">
                                    <label htmlFor="hallSelect">{t('trainings.trainingHall')}</label>
                                    <select
                                        id="hallSelect"
                                        name="hallId"
                                        value={bookingForm.hallId}
                                        onChange={handleBookingFormChange}
                                        className="modern-select"
                                    >
                                        {halls.map(hall => (
                                            <option key={hall.id} value={hall.id}>{hall.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div className="modern-input-group">
                                <label htmlFor="trainingDate">{t('trainings.date')}</label>
                                <CustomDatePicker
                                    id="trainingDate"
                                    name="date"
                                    value={bookingForm.date}
                                    onChange={handleBookingFormChange}
                                />
                            </div>

                            <div className="modern-time-inputs">
                                <div className="modern-input-group">
                                    <label htmlFor="startTime">{t('trainings.startTime')}</label>
                                    <CustomTimePicker
                                        id="startTime"
                                        name="startTime"
                                        value={bookingForm.startTime}
                                        onChange={handleBookingFormChange}
                                    />
                                </div>

                                <div className="modern-input-group">
                                    <label htmlFor="endTime">{t('trainings.endTime')}</label>
                                    <CustomTimePicker
                                        id="endTime"
                                        name="endTime"
                                        value={bookingForm.endTime}
                                        onChange={handleBookingFormChange}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="modern-modal-footer">
                            <button
                                className="modern-cancel-button"
                                onClick={() => setIsBookModalOpen(false)}
                            >
                                <FiX className="btn-icon" />
                                <span>{t('common:cancel')}</span>
                            </button>
                            <button
                                className="modern-save-button"
                                onClick={bookSingleTraining}
                            >
                                <FiSave className="btn-icon" />
                                <span>{t('trainings.book')}</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Book Multiple Trainings Modal */}
            {isMultipleBookModalOpen && (
                <div className="modern-modal-overlay">
                    <div className="modern-modal multiple-modal">
                        <div className="modern-modal-header">
                            <h2 className="modern-modal-title">{t('trainings.bookMultiple')}</h2>
                            <button
                                className="modern-close-btn"
                                onClick={() => setIsMultipleBookModalOpen(false)}
                            >
                                &times;
                            </button>
                        </div>

                        <div className="modern-modal-content">
                            {halls.length > 0 && (
                                <div className="modern-input-group">
                                    <label htmlFor="hallSelectMultiple">{t('trainings.trainingHall')}</label>
                                    <select
                                        id="hallSelectMultiple"
                                        name="hallId"
                                        value={multipleBookingForm.hallId}
                                        onChange={handleMultipleBookingFormChange}
                                        className="modern-select"
                                    >
                                        {halls.map(hall => (
                                            <option key={hall.id} value={hall.id}>{hall.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <h3 className="modern-slots-title">{t('trainings.trainingSlots')}</h3>

                            {multipleBookingForm.timeSlots.map((slot, index) => (
                                <div key={index} className="modern-time-slot">
                                    <div className="slot-header">
                                        <h4>{t('trainings.slot', { number: index + 1 })}</h4>
                                        {multipleBookingForm.timeSlots.length > 1 && (
                                            <button
                                                className="remove-slot-btn"
                                                onClick={() => removeTimeSlot(index)}
                                            >
                                                <FiX />
                                            </button>
                                        )}
                                    </div>

                                    <div className="modern-input-group">
                                        <label>{t('trainings.date')}</label>
                                        <CustomDatePicker
                                            name={`slot-date-${index}`}
                                            value={slot.date}
                                            onChange={(e) => handleTimeSlotChange(index, "date", e.target.value)}
                                        />
                                    </div>

                                    <div className="modern-time-inputs">
                                        <div className="modern-input-group">
                                            <label>{t('trainings.start')}</label>
                                            <CustomTimePicker
                                                name={`slot-start-${index}`}
                                                value={slot.startTime}
                                                onChange={(e) => handleTimeSlotChange(index, "startTime", e.target.value)}
                                            />
                                        </div>
                                        <div className="modern-input-group">
                                            <label>{t('trainings.end')}</label>
                                            <CustomTimePicker
                                                name={`slot-end-${index}`}
                                                value={slot.endTime}
                                                onChange={(e) => handleTimeSlotChange(index, "endTime", e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}

                            <button
                                className="modern-add-slot-btn"
                                onClick={addTimeSlot}
                            >
                                <FiPlus /> {t('trainings.addSlot')}
                            </button>
                        </div>

                        <div className="modern-modal-footer">
                            <button
                                className="modern-cancel-button"
                                onClick={() => setIsMultipleBookModalOpen(false)}
                            >
                                <FiX className="btn-icon" />
                                <span>{t('common:cancel')}</span>
                            </button>
                            <button
                                className="modern-save-button"
                                onClick={bookMultipleTrainings}
                            >
                                <FiSave className="btn-icon" />
                                <span>{t('trainings.bookTrainings')}</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Recurring Training Modal */}
            {isRecurringModalOpen && (
                <div className="modern-modal-overlay">
                    <div className="modern-modal recurring-modal">
                        <div className="modern-modal-header">
                            <h2 className="modern-modal-title">{t('trainings.bookRecurring')}</h2>
                            <button
                                className="modern-close-btn"
                                onClick={() => setIsRecurringModalOpen(false)}
                            >
                                &times;
                            </button>
                        </div>

                        <div className="modern-modal-content">
                            <div className="modern-input-group">
                                <label>{t('trainings.recurrencePattern')}</label>
                                <select
                                    value={recurringForm.patternType}
                                    onChange={(e) => setRecurringForm(prev => ({ ...prev, patternType: e.target.value }))}
                                    className="modern-select"
                                >
                                    <option value="WEEKLY">{t('trainings.weekly')}</option>
                                    <option value="BIWEEKLY">{t('trainings.biweekly')}</option>
                                    <option value="CUSTOM">{t('trainings.custom')}</option>
                                </select>
                            </div>

                            <div className="modern-input-group">
                                <label>{t('trainings.daysOfWeek')}</label>
                                <div className="days-of-week-selector">
                                    {['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'].map(day => (
                                        <button
                                            key={day}
                                            type="button"
                                            className={`day-btn ${recurringForm.daysOfWeek.includes(day) ? 'active' : ''}`}
                                            onClick={() => toggleDayOfWeek(day)}
                                        >
                                            {t(`trainings.days.${day.toLowerCase()}`)}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="modern-time-inputs">
                                <div className="modern-input-group">
                                    <label>{t('trainings.startTime')}</label>
                                    <CustomTimePicker
                                        name="recurringStartTime"
                                        value={recurringForm.startTime}
                                        onChange={(e) => setRecurringForm(prev => ({ ...prev, startTime: e.target.value }))}
                                    />
                                </div>
                                <div className="modern-input-group">
                                    <label>{t('trainings.endTime')}</label>
                                    <CustomTimePicker
                                        name="recurringEndTime"
                                        value={recurringForm.endTime}
                                        onChange={(e) => setRecurringForm(prev => ({ ...prev, endTime: e.target.value }))}
                                    />
                                </div>
                            </div>

                            <div className="modern-input-group">
                                <label>{t('trainings.startDate')}</label>
                                <CustomDatePicker
                                    name="recurringStartDate"
                                    value={recurringForm.startDate}
                                    onChange={(e) => setRecurringForm(prev => ({ ...prev, startDate: e.target.value }))}
                                />
                            </div>

                            <div className="modern-input-group">
                                <label>{t('trainings.endMethod')}</label>
                                <div className="end-type-selector">
                                    <label className="radio-label">
                                        <input
                                            type="radio"
                                            name="endType"
                                            value="BY_COUNT"
                                            checked={recurringForm.endType === "BY_COUNT"}
                                            onChange={(e) => setRecurringForm(prev => ({ ...prev, endType: e.target.value }))}
                                        />
                                        {t('trainings.byCount')}
                                    </label>
                                    <label className="radio-label">
                                        <input
                                            type="radio"
                                            name="endType"
                                            value="BY_DATE"
                                            checked={recurringForm.endType === "BY_DATE"}
                                            onChange={(e) => setRecurringForm(prev => ({ ...prev, endType: e.target.value }))}
                                        />
                                        {t('trainings.byDate')}
                                    </label>
                                </div>
                            </div>

                            {recurringForm.endType === "BY_COUNT" ? (
                                <div className="modern-input-group">
                                    <label>{t('trainings.occurrenceCount')}</label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="52"
                                        value={recurringForm.occurrenceCount}
                                        onChange={(e) => setRecurringForm(prev => ({ ...prev, occurrenceCount: e.target.value }))}
                                        className="modern-input"
                                    />
                                </div>
                            ) : (
                                <div className="modern-input-group">
                                    <label>{t('trainings.endDate')}</label>
                                    <CustomDatePicker
                                        name="recurringEndDate"
                                        value={recurringForm.endDate}
                                        onChange={(e) => setRecurringForm(prev => ({ ...prev, endDate: e.target.value }))}
                                    />
                                </div>
                            )}

                            {halls.length > 0 && (
                                <div className="modern-input-group">
                                    <label>{t('trainings.trainingHall')}</label>
                                    <select
                                        value={recurringForm.hallId}
                                        onChange={(e) => setRecurringForm(prev => ({ ...prev, hallId: e.target.value }))}
                                        className="modern-select"
                                    >
                                        {halls.map(hall => (
                                            <option key={hall.id} value={hall.id}>{hall.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div className="modern-input-group">
                                <label>{t('trainings.notesOptional')}</label>
                                <textarea
                                    value={recurringForm.notes}
                                    onChange={(e) => setRecurringForm(prev => ({ ...prev, notes: e.target.value }))}
                                    rows="2"
                                />
                            </div>
                        </div>

                        <div className="modern-modal-footer">
                            <button
                                className="modern-cancel-button"
                                onClick={() => setIsRecurringModalOpen(false)}
                            >
                                <FiX className="btn-icon" />
                                <span>{t('common:cancel')}</span>
                            </button>
                            <button
                                className="modern-save-button"
                                onClick={bookRecurringTrainings}
                                disabled={recurringSubmitting}
                            >
                                <FiRepeat className="btn-icon" />
                                <span>{recurringSubmitting ? t('trainings.saving') : t('trainings.bookRecurring')}</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Training Modal */}
            {isEditModalOpen && currentTraining && (
                <div className="modern-modal-overlay">
                    <div className="modern-modal">
                        <div className="modern-modal-header">
                            <h2 className="modern-modal-title">{t('trainings.editTraining')}</h2>
                            <button
                                className="modern-close-btn"
                                onClick={() => setIsEditModalOpen(false)}
                            >
                                &times;
                            </button>
                        </div>

                        <div className="modern-modal-content">
                            {halls.length > 0 && (
                                <div className="modern-input-group">
                                    <label htmlFor="editHallSelect">{t('trainings.trainingHall')}</label>
                                    <select
                                        id="editHallSelect"
                                        name="hallId"
                                        value={editForm.hallId}
                                        onChange={handleEditFormChange}
                                        className="modern-select"
                                    >
                                        {halls.map(hall => (
                                            <option key={hall.id} value={hall.id}>{hall.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div className="modern-input-group">
                                <label htmlFor="editDate">{t('trainings.date')}</label>
                                <CustomDatePicker
                                    id="editDate"
                                    name="date"
                                    value={editForm.date}
                                    onChange={handleEditFormChange}
                                />
                            </div>

                            <div className="modern-time-inputs">
                                <div className="modern-input-group">
                                    <label htmlFor="editStartTime">{t('trainings.startTime')}</label>
                                    <CustomTimePicker
                                        id="editStartTime"
                                        name="startTime"
                                        value={editForm.startTime}
                                        onChange={handleEditFormChange}
                                    />
                                </div>

                                <div className="modern-input-group">
                                    <label htmlFor="editEndTime">{t('trainings.endTime')}</label>
                                    <CustomTimePicker
                                        id="editEndTime"
                                        name="endTime"
                                        value={editForm.endTime}
                                        onChange={handleEditFormChange}
                                    />
                                </div>
                            </div>

                            <div className="modern-input-group">
                                <label htmlFor="editNotes">{t('trainings.notesOptional')}</label>
                                <textarea
                                    id="editNotes"
                                    name="notes"
                                    value={editForm.notes}
                                    onChange={handleEditFormChange}
                                    rows="3"
                                />
                            </div>
                        </div>

                        <div className="modern-modal-footer">
                            <button
                                className="modern-cancel-button"
                                onClick={() => setIsEditModalOpen(false)}
                            >
                                <FiX className="btn-icon" />
                                <span>{t('common:cancel')}</span>
                            </button>
                            <button
                                className="modern-save-button"
                                onClick={updateTraining}
                            >
                                <FiSave className="btn-icon" />
                                <span>{t('trainings.saveChanges')}</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {isDeleteModalOpen && currentTraining && (
                <div className="modern-modal-overlay">
                    <div className="modern-modal confirmation-modal">
                        <div className="modern-modal-header">
                            <h2 className="modern-modal-title">{t('trainings.confirmDelete')}</h2>
                            <button
                                className="modern-close-btn"
                                onClick={() => setIsDeleteModalOpen(false)}
                            >
                                &times;
                            </button>
                        </div>

                        <div className="modern-modal-content">
                            <p className="confirmation-message">
                                {t('trainings.confirmDeleteMessage', { date: formatDate(currentTraining.scheduledAt), time: formatTime(currentTraining.scheduledAt) })}
                            </p>
                            <p className="warning-message">
                                {t('trainings.irreversible')}
                            </p>
                        </div>

                        <div className="modern-modal-footer">
                            <button
                                className="modern-cancel-button"
                                onClick={() => setIsDeleteModalOpen(false)}
                            >
                                {t('common:cancel')}
                            </button>
                            <button
                                className="modern-delete-btn"
                                onClick={deleteTraining}
                            >
                                {t('trainings.deleteTraining')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Attendance / Complete Training Modal */}
            {isAttendanceModalOpen && currentTraining && (
                <div className="modern-modal-overlay">
                    <div className="modern-modal attendance-modal">
                        <div className="modern-modal-header">
                            <h2 className="modern-modal-title">
                                {isCompleteConfirmOpen
                                    ? t('trainings.completeAndAttendance')
                                    : t('trainings.attendance')
                                }
                            </h2>
                            <button
                                className="modern-close-btn"
                                onClick={() => {
                                    setIsAttendanceModalOpen(false);
                                    setIsCompleteConfirmOpen(false);
                                }}
                            >
                                &times;
                            </button>
                        </div>

                        <div className="modern-modal-content">
                            <div className="attendance-training-info">
                                <span className="attendance-date">
                                    <FiCalendar /> {formatDate(currentTraining.scheduledAt)}
                                </span>
                                <span className="attendance-time">
                                    <FiClock /> {formatTime(currentTraining.scheduledAt)} - {getEndTime(currentTraining)}
                                </span>
                            </div>

                            {isCompleteConfirmOpen && (
                                <p className="attendance-hint">
                                    {t('trainings.completeHint')}
                                </p>
                            )}

                            <div className="attendance-list">
                                {attendanceData.length === 0 ? (
                                    <div className="modern-empty-list">
                                        {t('trainings.noDogs')}
                                    </div>
                                ) : (
                                    attendanceData.map((dp) => {
                                        const isAbsent = dp.newStatus === "ABSENT";
                                        const isReadOnly = currentTraining.status === "COMPLETED";

                                        return (
                                            <div key={dp.dogId} className={`attendance-item ${isAbsent ? 'absent' : 'present'}`}>
                                                <div className="attendance-dog-info">
                                                    <div className={`modern-avatar small ${isAbsent ? '' : 'dog-avatar'}`}>
                                                        {dp.dogName?.charAt(0) || '?'}
                                                    </div>
                                                    <div className="attendance-dog-details">
                                                        <span className="attendance-dog-name">{dp.dogName}</span>
                                                        {dp.breed && <span className="attendance-dog-breed">{dp.breed}</span>}
                                                        {dp.ownerName && (
                                                            <span className="attendance-owner">{t('caretaker')}: {dp.ownerName}</span>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="attendance-controls">
                                                    {!isReadOnly ? (
                                                        <button
                                                            className={`attendance-toggle ${isAbsent ? 'absent' : 'present'}`}
                                                            onClick={() => toggleAttendance(dp.dogId)}
                                                        >
                                                            {isAbsent ? (
                                                                <><FiUserX /> {t('trainings.absent')}</>
                                                            ) : (
                                                                <><FiCheck /> {t('trainings.present')}</>
                                                            )}
                                                        </button>
                                                    ) : (
                                                        <span className={`attendance-status-badge ${dp.status === "ABSENT" ? 'absent' : 'present'}`}>
                                                            {dp.status === "ABSENT"
                                                                ? t('trainings.absent')
                                                                : dp.status === "ATTENDED"
                                                                    ? t('trainings.present')
                                                                    : t('trainings.scheduled')
                                                            }
                                                        </span>
                                                    )}
                                                </div>

                                                {isAbsent && !isReadOnly && (
                                                    <div className="absence-note-field">
                                                        <input
                                                            type="text"
                                                            placeholder={t('trainings.absenceNotePlaceholder')}
                                                            value={attendanceNotes[dp.dogId] || ''}
                                                            onChange={(e) => setAttendanceNotes(prev => ({
                                                                ...prev,
                                                                [dp.dogId]: e.target.value
                                                            }))}
                                                        />
                                                    </div>
                                                )}

                                                {dp.status === "ABSENT" && isReadOnly && dp.absenceNote && (
                                                    <div className="absence-note-display">
                                                        {dp.absenceNote}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>

                        <div className="modern-modal-footer">
                            <button
                                className="modern-cancel-button"
                                onClick={() => {
                                    setIsAttendanceModalOpen(false);
                                    setIsCompleteConfirmOpen(false);
                                }}
                            >
                                {t('common:cancel')}
                            </button>
                            {currentTraining.status !== "COMPLETED" && (
                                <>
                                    {isCompleteConfirmOpen ? (
                                        <button
                                            className="modern-save-button complete-btn"
                                            onClick={completeTraining}
                                            disabled={savingAttendance}
                                        >
                                            <FiCheckCircle className="btn-icon" />
                                            <span>{savingAttendance ? t('trainings.saving') : t('trainings.completeTraining')}</span>
                                        </button>
                                    ) : (
                                        <button
                                            className="modern-save-button"
                                            onClick={saveAttendance}
                                            disabled={savingAttendance}
                                        >
                                            <FiSave className="btn-icon" />
                                            <span>{savingAttendance ? t('trainings.saving') : t('trainings.saveAttendance')}</span>
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GroupTrainings;
